import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Link as LinkIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import { useSnackbar } from 'notistack';

export default function TelegramChannels() {
  const [channels, setChannels] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    description: '',
    isActive: true
  });
  const { enqueueSnackbar } = useSnackbar();

  // Загрузка списка каналов
  const fetchChannels = async () => {
    try {
      const response = await axios.get('/telegram-channels');
      setChannels(response.data);
    } catch (error) {
      enqueueSnackbar('Ошибка при загрузке списка каналов', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  // Открытие диалога для создания/редактирования
  const handleOpenDialog = (channel = null) => {
    if (channel) {
      setEditingChannel(channel);
      setFormData({
        name: channel.name,
        link: channel.link,
        description: channel.description || '',
        isActive: channel.isActive
      });
    } else {
      setEditingChannel(null);
      setFormData({
        name: '',
        link: '',
        description: '',
        isActive: true
      });
    }
    setIsDialogOpen(true);
  };

  // Закрытие диалога
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingChannel(null);
    setFormData({
      name: '',
      link: '',
      description: '',
      isActive: true
    });
  };

  // Изменение полей формы
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value,
    }));
  };

  // Сохранение канала
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingChannel) {
        await axios.put(`/telegram-channels/${editingChannel._id}`, formData);
        enqueueSnackbar('Канал успешно обновлен', { variant: 'success' });
      } else {
        await axios.post('/telegram-channels', formData);
        enqueueSnackbar('Канал успешно добавлен', { variant: 'success' });
      }
      handleCloseDialog();
      fetchChannels();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Ошибка при сохранении канала',
        { variant: 'error' }
      );
    }
  };

  // Удаление канала
  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот канал?')) {
      try {
        await axios.delete(`/telegram-channels/${id}`);
        fetchChannels();
        enqueueSnackbar('Канал успешно удален', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Ошибка при удалении канала', { variant: 'error' });
      }
    }
  };

  // Переключение статуса
  const handleToggleStatus = async (id) => {
    try {
      await axios.patch(`/telegram-channels/${id}/toggle-status`);
      fetchChannels();
      enqueueSnackbar('Статус канала изменен', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Ошибка при изменении статуса', { variant: 'error' });
    }
  };

  return (
    <Container>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Telegram каналы
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить канал
        </Button>
      </Box>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Ссылка</TableCell>
                <TableCell>Описание</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map((channel) => (
                <TableRow key={channel._id}>
                  <TableCell>{channel.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinkIcon fontSize="small" color="action" />
                      <a 
                        href={channel.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {channel.link}
                      </a>
                    </Box>
                  </TableCell>
                  <TableCell>{channel.description || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={channel.isActive ? 'Активен' : 'Неактивен'} 
                      color={channel.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleStatus(channel._id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenDialog(channel)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(channel._id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Диалог создания/редактирования */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingChannel ? 'Редактировать канал' : 'Добавить канал'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Название канала"
              type="text"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="link"
              label="Ссылка на канал"
              type="url"
              fullWidth
              value={formData.link}
              onChange={handleChange}
              required
              helperText="Например: https://t.me/your_channel"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="description"
              label="Описание"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
              }
              label="Активен"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">
              {editingChannel ? 'Сохранить' : 'Добавить'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}
